/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2014-2017, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

/**
 * A service used to fetch and store groups that the current account is a member of.
 * All instance of the GroupController should use this service instead of the BulbAjaxService.
 */
angular.module('bulbApp.groupModule')
  .factory('BulbGroupService', [
    '$filter',
    '$q',
    '$translate',
    'AeroDialogService',
    'Restangular',
    'BulbAuthService',
    'BulbErrorService',
    'GROUP',
    '_',
    BulbGroupService,
  ]);

function BulbGroupService($filter, $q, $translate, AeroDialogService, Restangular, BulbAuthService, BulbErrorService, GROUP, _) {
  return {
    /**
     * An array of all retrieved groups.
     */
    groups: [],

    /**
     * Indicates whether or not all possible groups have been fetched for this toggle. This value will
     * automatically reset every time the group list is changed from open to closed.
     */
    fetchedAllGroupsForToggle: false,

    /**
     * Says what the current status is for the groups list.
     */
    currentStatus: 'open',

    /**
     * Shows us whether or not the group list was toggled.
     */
    groupListWasToggled: false,

    /**
     * Deleted groups using dev tool.
     */
    deletedGroupUsingDevTool: false,

    /**
     * General options for when someone is trying to close a group, so we don't have to duplicate these
     * in multiple places if someone is trying to close their group from different locations of the app.
     */
    closeGroupModalOptions: {
      title: $translate.instant('close_group_modal_title'),
      icon: 'fas fa-minus-octagon',
      iconClass: 'aero-alert',
      detail: $translate.instant('close_group_modal_body_text'),
      primaryLabel: $translate.instant('close_group_button_text'),
      primaryStyle: AeroDialogService.getButtonStyleEnum().NEGATIVE_ACTION,
    },

    /**
     * A status indicator if we're filtering for only open groups. We can also use this to determine if
     * we've toggled the status from open groups to closed or vice versa.
     */
    openGroupFilterStatusForGroupHeader: true,

    /**
     * Fetches a page of groups.
     * @param after
     * @param size
     * @param groupStatusToSearch if this is true or false, it means we're fetching either open or closed groups.
     * @returns the promise.
     */
    fetchGroups: function fetchGroups(after, size, groupStatusToSearch) {
      const that = this;
      groupStatusToSearch = (groupStatusToSearch === 'undefined') ? null : that.currentStatus;

      if (after) {
        // First check if the after sortkey is already in the group array.
        // Then check if there are enough groups after that.
      }

      // FUTURE: group size should not be required. If no size is provided, all groups should be returned.
      if (!size) {
        size = GROUP.PAGE_SIZE;
      }

      // If the requested groups are not available, fetch them from the server.
      return Restangular.all('groups').getList({
        size,
        after,
        memberId: BulbAuthService.currentUser.id,
        filter: groupStatusToSearch,
      }).then((data) => {
        // We check to see if the data that is returned is less than the size allotment. If it is, that means
        // that we've acquired all of the groups we can for the current toggled list.
        if (data.length < size) {
          that.fetchedAllGroupsForToggle = true;
        }

        if (data && data.length > 0) {
          // Check if it explicitly equals false, since it can be undefined.
          if (groupStatusToSearch === 'open' || groupStatusToSearch === 'closed') {
            if (!that.groupListWasToggled) {
              // This means no toggle has occurred. So instead of wiping the list and starting over,
              // we need to just concat the list.
              that.groups = that.groups.concat(data);
            } else {
              // This means we've toggled the list, and we need to start clean with a new list of groups.
              that.groups = data;
              that.groupListWasToggled = false;
            }
          } else {
            that.groups = that.groups.concat(data);
          }
          // Make sure there aren't any duplicates in the groups array, nor the filteredGroups array:
          that.groups = _.uniq(that.groups, 'id');
          that.groups = $filter('orderBy')(that.groups, 'sortKey');
        } else {
          // There were no new groups, but there was a toggle. So set groups to empty here.
          if (that.groupListWasToggled) {
            that.groups = [];
            that.groupListWasToggled = false;
          }
          if (that.deletedGroupUsingDevTool) {
            that.groups = [];
            that.groupListWasToggled = false;
          }
        }
      }, (error) => {
        BulbErrorService.error('Group service fetch groups error.', error);
      });
    },

    /**
     * If a group that occurs in the list has been updated, update the copy in the list too.
     * If it hasn't been fetched yet, assume it will already have been updated when it is finally fetched.
     * FUTURE: some more general way of updating interested components?
     * @param group GroupDTO for the group that has been updated.
     */
    updateGroup: function updateGroup(group) {
      for (let i = 0; i < this.groups.length; i += 1) {
        if (this.groups[i].id === group.id) {
          this.groups[i] = group;
          return;
        }
      }
    },

    /**
     * Create a group.
     * @param group
     * @returns {Deferred}
     */
    createGroup: function createGroup(group) {
      const deferred = $q.defer();
      const that = this;
      Restangular.all('groups').post(group).then((group) => {
        that.groups.push(group);
        that.groups = $filter('orderBy')(that.groups, 'sortKey');
        BulbAuthService.currentUser.features.GroupFeature.currentGroups += 1;
        deferred.resolve(group);
      }, (error) => {
        deferred.reject(error);
      });
      return deferred.promise;
    },

    /**
     * Create a MacMillan Group.
     *
     * @param selectedBook The selected book for the MacMillan Group.
     * @returns {*} Promise resolving to the newly created MacMillan Group.
     */
    createMacMillanGroup: function createMacMillanGroup(selectedBook) {
      const deferred = $q.defer();
      const that = this;
      Restangular.all('groups').customPOST({}, '', { macmillan: selectedBook }).then((macMillanGroup) => {
        that.groups.push(macMillanGroup);
        that.groups = $filter('orderBy')(that.groups, 'sortKey');
        BulbAuthService.currentUser.features.GroupFeature.currentGroups += 1;
        deferred.resolve(macMillanGroup);
      }, (error) => {
        deferred.reject(error);
      });
      return deferred.promise;
    },

    /**
     * Add joined group to list.
     * @param group
     */
    joinedGroup: function joinedGroup(group) {
      this.groups.push(group);
    },

    /**
     * Return the remaining groups for the user.
     * @returns Remaining available groups
     */
    getRemainingGroups: function getRemainingGroups() {
      if (BulbAuthService.currentUser.id) {
        if (BulbAuthService.currentUser.features.GroupFeature.maxGroups < 0) {
          return Number.MAX_VALUE;
        }
        return (BulbAuthService.currentUser.features.GroupFeature.maxGroups
          - BulbAuthService.currentUser.features.GroupFeature.currentGroups);
      }
      return 0;
    },

    /**
     * Create a classroom group.
     * @param course
     * @returns {Deferred}
     */
    createClassroomGroup: function createClassroomGroup(course, authToken) {
      const deferred = $q.defer();
      const that = this;
      Restangular.one('groups')
        .customPOST(course, '', { token: authToken, type: 'google_classroom' })
        .then((group) => {
          that.groups.push(group);
          that.groups = $filter('orderBy')(that.groups, 'sortKey');
          BulbAuthService.currentUser.features.GroupFeature.currentGroups += 1;
          deferred.resolve(group);
        }, (error) => {
          deferred.reject(error);
        });
      return deferred.promise;
    },

    /**
     * re sync  existing classroom group.
     * @param groupId Id of the group
     * @param courseId Id of the classroom course
     * @param authToken authentication token
     * @returns {Deferred}
     */
    reSyncClassroomGroup: function reSyncClassroomGroup(groupId, courseId, authToken) {
      const deferred = $q.defer();
      const that = this;
      Restangular.one(`groups/${groupId}`)
        .patch('', { token: authToken, type: 'google_classroom' })
        .then((group) => {
          that.updateGroup(group);
          deferred.resolve(group);
        }, (error) => {
          deferred.reject(error);
        });
      return deferred.promise;
    },

    /**
     * This function will toggle the group status from open to closed, or vice versa. It will make it so that
     * we can close groups from multiple places in the app, rather than just the group page like before.
     * @param group the group we want to toggle the status of.
     * @returns a promise that can be resolved to get the real closed group.
     */
    toggleGroupStatus: function toggleGroupStatus(group) {
      const activate = !group.active;
      const that = this;

      return Restangular.one('groups', group.id).patch({ active: activate }).then((returnedGroup) => {
        // Add/remove toggled group from list of groups
        const indexOfGroup = _.findIndex(that.groups, { id: returnedGroup.id });

        if (indexOfGroup === -1) {
          that.groups.push(returnedGroup);
          that.groups = $filter('orderBy')(that.groups, 'sortKey');
        } else {
          that.groups.splice(indexOfGroup, 1);
        }

        if (activate) {
          BulbAuthService.currentUser.features.GroupFeature.currentGroups += 1;
        } else {
          BulbAuthService.currentUser.features.GroupFeature.currentGroups -= 1;
        }

        return {
          updatedGroup: returnedGroup,
          remainingGroups: that.getRemainingGroups(),
        };
      }, (error) => {
        BulbErrorService.error('Cannot change the active status for group', error);
      });
    },

    /**
     * This function will return the last date that the group was closed.
     * @param group the group to check against.
     * @returns string|null or the last date the group was closed.
     */
    getDateGroupWasClosed: function getDateGroupWasClosed(group) {
      if (group.lastDateClosed) {
        return new Date(group.lastDateClosed)
          .toLocaleDateString(undefined, {
            day: '2-digit', month: '2-digit', year: 'numeric',
          });
      }

      return null;
    },

    /**
     * This function will check if current user is an org member or not.
     * @returns true if user part of an org else false.
     */
    accountIsInOrg: function accountIsInOrg() {
      return (BulbAuthService.currentUser.groupMemberships
        .findIndex((index) => index.type === 'ORGANIZATION')) !== -1;
    },
  };
}
